/**
 * Mystery Gift auto-add/remove reconciler.
 *
 * Watches the theme's cart:rerendered / product:added events and keeps the
 * "Mystery Gift" (_free_gift) line in sync with the qualifying subtotal, in
 * every presentment currency. Correctness rests solely on the gift line's
 * final_line_price — 0 means the live Bxgy discount actually zeroed it, in
 * every currency, with no FX math required. A gift that isn't zeroed is
 * removed, full stop. The add-trigger below is a best-effort, Markets-
 * converted optimization to reduce add/remove flashing; it never gates
 * correctness.
 *
 * Config comes from window.scsFreeGift (snippets/scs-custom-liquid.liquid).
 * No window.fetch wrapping here — cart-add/cart-update in custom.js already
 * own that chain; this reconciler drives /cart/add.js and /cart/change.js
 * directly through window.fetch so the existing interceptors still see it.
 */
(() => {
  const cfg = window.scsFreeGift;
  if (!cfg || !cfg.enabled || !cfg.variantId) return;

  const DECLINE_KEY = 'scsFreeGiftDeclined';
  const DEBOUNCE_MS = 200;

  let inFlight = false;
  let debounceTimer = null;
  let lastGiftPresent = null;

  // Ineligible floor: the qualifying subtotal at which the gift last came back
  // not-free. Keyed to cart.currency so a shopper switching markets mid-session
  // doesn't carry a stale EUR floor into a JPY cart.
  let ineligibleFloor = 0;
  let ineligibleFloorCurrency = null;

  function inPromoWindow() {
    const now = Date.now();
    const start = cfg.startsAt ? new Date(cfg.startsAt) : null;
    const end = cfg.endsAt ? new Date(cfg.endsAt) : null;
    if (start && !isNaN(start.getTime()) && now < start.getTime()) return false;
    if (end && !isNaN(end.getTime()) && now > end.getTime()) return false;
    return true;
  }

  function isDeclined() {
    try {
      return sessionStorage.getItem(DECLINE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDeclined() {
    try {
      sessionStorage.setItem(DECLINE_KEY, '1');
    } catch (e) {
      // sessionStorage unavailable (private mode, quota) — degrade to "not declined"
    }
  }

  function clearDeclined() {
    try {
      sessionStorage.removeItem(DECLINE_KEY);
    } catch (e) {
      // sessionStorage unavailable — nothing to clear
    }
  }

  function dispatchCartRefresh() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function reconcile() {
    if (inFlight) return;
    if (!cfg.enabled || !cfg.variantId || !inPromoWindow()) return;

    inFlight = true;
    try {
      let cart;
      try {
        const resp = await window.fetch('/cart.js');
        if (!resp.ok) throw new Error('cart fetch failed');
        cart = await resp.json();
      } catch (e) {
        console.error('[scs-free-gift] cart fetch failed:', e);
        return;
      }

      // Floor is per-currency: a market switch mid-session invalidates it.
      if (cart.currency !== ineligibleFloorCurrency) {
        ineligibleFloor = 0;
        ineligibleFloorCurrency = cart.currency;
      }

      let qualifyingSubtotal = 0;
      let giftLine = null;
      cart.items.forEach(item => {
        const isGift = item.properties?.['_free_gift'] === '1' || String(item.variant_id) === String(cfg.variantId);
        if (isGift) {
          giftLine = item;
        } else {
          qualifyingSubtotal += item.line_price;
        }
      });

      const giftInCart = !!giftLine;

      // Sole correctness gate, every currency: the live Bxgy discount is the only
      // authority on whether the gift is actually free. 0 is 0 in every currency and
      // decimal representation, so this needs no FX math. If it isn't zeroed, the
      // discount didn't apply (below the real converted threshold, or ineligible) —
      // the gift comes out, and this can never be overridden by an FX-derived value.
      if (giftInCart && giftLine.final_line_price > 0) {
        try {
          const removeResp = await window.fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftLine.key, quantity: 0 })
          });
          if (!removeResp.ok) throw new Error('remove failed');
          ineligibleFloor = qualifyingSubtotal;
          lastGiftPresent = false;
          dispatchCartRefresh();
        } catch (e) {
          console.error('[scs-free-gift] non-free gift removal failed:', e);
        }
        return;
      }

      // Optional add-trigger: a Markets-converted threshold derived from the gift's
      // own converted price, scaled by the fixed USD ratio (threshold / gift price).
      // Prefer the gift line's own price when it's present — the most reliable
      // presentment source — otherwise fall back to the Liquid-rendered price. This
      // is advisory only; the verification above is what guarantees correctness.
      const presentmentPrice = giftInCart ? giftLine.price : cfg.giftPresentmentPrice;
      const giftUsdPrice = cfg.giftPriceUsd || 695;
      let convertedThresholdCents = 0;
      if (typeof presentmentPrice === 'number' && isFinite(presentmentPrice) && presentmentPrice > 0) {
        convertedThresholdCents = Math.round(presentmentPrice * (cfg.threshold * 100) / giftUsdPrice);
      }
      // Else: optimistic fallback (convertedThresholdCents stays 0) — any non-empty
      // qualifying cart triggers an add, and the verification gate above cleans up if
      // the discount doesn't apply. Accept minor flashing in weak currencies; this
      // never affects correctness.

      const qualifies = convertedThresholdCents > 0
        ? qualifyingSubtotal >= convertedThresholdCents
        : qualifyingSubtotal > 0;

      // Manual-removal detection: gift was present, is now gone, and the cart still
      // qualifies — the reconciler only ever removes a non-free gift or a below-
      // threshold one, so it can't have caused this. Respect the customer's choice
      // while the cart stays qualifying.
      if (lastGiftPresent === true && !giftInCart && qualifies) {
        setDeclined();
      }

      // Softened decline: the flag only holds while the cart qualifies. Once it drops
      // below threshold (or empties out, in optimistic fallback), reset so a rebuilt
      // qualifying cart re-offers the gift.
      if (!qualifies) {
        clearDeclined();
      }

      let nextGiftPresent = giftInCart;

      if (qualifies && qualifyingSubtotal > ineligibleFloor && !giftInCart && !isDeclined()) {
        try {
          const addResp = await window.fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: cfg.variantId,
              quantity: 1,
              properties: { _free_gift: '1' }
            })
          });
          if (!addResp.ok) throw new Error('add failed');
          nextGiftPresent = true;
          dispatchCartRefresh();
        } catch (e) {
          console.error('[scs-free-gift] auto-add failed:', e);
        }
      } else if (!qualifies && giftInCart) {
        try {
          const removeResp = await window.fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftLine.key, quantity: 0 })
          });
          if (!removeResp.ok) throw new Error('remove failed');
          nextGiftPresent = false;
          dispatchCartRefresh();
        } catch (e) {
          console.error('[scs-free-gift] auto-remove failed:', e);
        }
      }

      lastGiftPresent = nextGiftPresent;
    } finally {
      inFlight = false;
    }
  }

  function scheduleReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  // Capture phase: the mini-cart drawer dispatches cart:rerendered WITHOUT bubbles on the
  // drawer element (see _rerender in theme.js), so a bubble-phase document listener misses
  // drawer quantity changes. Capture fires on document for non-bubbling descendant events too.
  document.addEventListener('cart:rerendered', scheduleReconcile, true);
  document.addEventListener('product:added', scheduleReconcile, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile);
  } else {
    scheduleReconcile();
  }
})();
